import React, { useState } from "react"
import styled from "styled-components"
import { Box, IconButton, Typography } from "@mui/material"
import {
  Close as CloseIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Announcement as AnnouncementIcon,
} from "@mui/icons-material"

export type BannerLevel =
  | "info"
  | "warning"
  | "error"
  | "success"
  | "announcement"

interface AnnouncementBannerProps {
  message: string
  level?: BannerLevel
  dismissible?: boolean
  onDismiss?: () => void
  actionText?: string
  onActionClick?: () => void
  id?: string
}

const getBannerStyles = (level: BannerLevel) => {
  const styles = {
    info: {
      backgroundColor: "rgba(227, 250, 253, 1)",
      color: "var(--color-teal-dark)",
      borderColor: "var(--color-teal-dark)",
    },
    success: {
      backgroundColor: "#E8F5E8",
      color: "#2E7D32",
      borderColor: "#4CAF50",
    },
    warning: {
      backgroundColor: "#FFF3E0",
      color: "#E65100",
      borderColor: "#FF9800",
    },
    error: {
      backgroundColor: "#FFEBEE",
      color: "#C62828",
      borderColor: "#F44336",
    },
    announcement: {
      backgroundColor: "#F3E5F5",
      color: "var(--color-purple-dark)",
      borderColor: "var(--color-purple-dark)",
    },
  }
  return styles[level] || styles.info
}

const getBannerIcon = (level: BannerLevel) => {
  const icons = {
    info: InfoIcon,
    success: InfoIcon,
    warning: WarningIcon,
    error: ErrorIcon,
    announcement: AnnouncementIcon,
  }
  return icons[level] || InfoIcon
}

const BannerContainer = styled(Box)<{ level: BannerLevel }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px 16px;
  border-left: 14px solid ${props => getBannerStyles(props.level).borderColor};
  background-color: ${props => getBannerStyles(props.level).backgroundColor};
  color: ${props => getBannerStyles(props.level).color};
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 1000;

  @media (max-width: 768px) {
    padding: 10px 12px;
    flex-direction: column;
    align-items: center;
    gap: 8px;
  }
`

const ContentWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  flex: 1;

  @media (max-width: 768px) {
    width: 100%;
    justify-content: center;
  }
`

const MessageWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  text-align: center;
  padding-right: 40px;

  @media (max-width: 768px) {
    padding-right: 48px;
  }
`

const ActionButton = styled.button<{ level: BannerLevel }>`
  background: transparent;
  border: 1px solid ${props => getBannerStyles(props.level).color};
  color: ${props => getBannerStyles(props.level).color};
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => getBannerStyles(props.level).color};
    color: ${props => getBannerStyles(props.level).backgroundColor};
  }

  @media (max-width: 768px) {
    align-self: flex-end;
  }
`

const AnnouncementBanner: React.FC<AnnouncementBannerProps> = ({
  message,
  level = "info",
  dismissible = true,
  onDismiss,
  actionText,
  onActionClick,
  id,
}) => {
  const [isVisible, setIsVisible] = useState(true)
  const IconComponent = getBannerIcon(level)
  const styles = getBannerStyles(level)

  const handleDismiss = () => {
    setIsVisible(false)
    if (onDismiss) {
      onDismiss()
    }

    // Store dismissal in localStorage if ID is provided
    if (id) {
      localStorage.setItem(`banner-dismissed-${id}`, "true")
    }
  }

  // Check if banner was previously dismissed
  React.useEffect(() => {
    if (id) {
      const wasDismissed = localStorage.getItem(`banner-dismissed-${id}`)
      if (wasDismissed === "true") {
        setIsVisible(false)
        // Also notify parent that this banner should not be counted
        if (onDismiss) {
          onDismiss()
        }
      }
    }
  }, [id, onDismiss])

  if (!isVisible) {
    return null
  }

  return (
    <BannerContainer level={level}>
      <ContentWrapper>
        <MessageWrapper>
          <IconComponent sx={{ fontSize: 20 }} />
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, textAlign: "center" }}
          >
            {message}
          </Typography>
        </MessageWrapper>

        {actionText && onActionClick && (
          <ActionButton level={level} onClick={onActionClick}>
            {actionText}
          </ActionButton>
        )}
      </ContentWrapper>

      {dismissible && (
        <IconButton
          size="small"
          onClick={handleDismiss}
          sx={{
            color: styles.color,
            position: "absolute",
            right: { xs: 16, sm: 16 },
            "&:hover": {
              backgroundColor: `${styles.color}20`,
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </BannerContainer>
  )
}

export default AnnouncementBanner
